import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { View, Text } from '@smarterservices/smarter-ui';
import {
  usePlayerStore,
  getStore,
  useLayoutStore,
} from '../../../stores/StoreContext';
import PropTypes from 'prop-types';
import HlsViewer from '../../Common/HlsViewer';

// This component uses CSS positioning to show specific regions of the HLS video stream
// without using canvas drawing by sharing the MediaStream from the main video element
const Hls = forwardRef(
  (
    {
      SessionTrack,
      isFocusedTrack = false,
      width = '100%',
      height = '100%',
      style = {},
      ...props
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const parentRef = useRef(null);
    const [viewPortSize, setViewPortSize] = useState({
      width: 500,
      height: 500,
    });
    const [resizeBasedOn, setResizeBasedOn] = useState('');
    const [containerStyle, setContainerStyle] = useState({});
    const [videoStyle, setVideoStyle] = useState({});
    const [maxVideoSize, setMaxVideoSize] = useState({ width: 0, height: 0 });
    const [parentSize, setParentSize] = useState({ width: 0, height: 0 });
    const [videoHeight, setVideoHeight] = useState(100);
    const [positioning, setPositioning] = useState({
      left: 0,
      top: 0,
      width: 640,
      height: 360,
      sessionChangeTime: 0,
      id: 'default',
    });

    const isPlaying = usePlayerStore((state) => state.playing);
    const currentTime = usePlayerStore((state) => state.currentTime);
    const tracks = usePlayerStore((state) => state.tracks);
    const seekCounter = usePlayerStore((state) => state.seekCounter);
    const videoContainerSize = useLayoutStore(
      (state) => state.containers.video.size
    );
    const controlsContainerSize = useLayoutStore(
      (state) => state.containers.controls.size
    );
    const PlayerStore = getStore('playerStore');
    
    // Expose the video element ref
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(videoRef.current);
        } else {
          ref.current = videoRef.current;
        }
      }
    }, [ref, videoRef]);

    // ensure the mute state is correct when a new video loads...
    useEffect(() => {
      if (!PlayerStore.hlsVideoRef.current) return;
      PlayerStore.hlsVideoRef.current.muted = PlayerStore.muted;
    }, [PlayerStore.hlsVideoRef.current]);

    // Get the current positioning based on the current time
    const getCurrentPositioning = () => {
      if (
        !SessionTrack?.hls?.track?.positions ||
        SessionTrack.hls.track.positions.length === 0
      ) {
        // Return default positioning if no positions are available
        return {
          left: 0,
          top: 0,
          width: 640,
          height: 360,
          sessionChangeTime: 0,
          id: 'default',
        };
      }

      // Sort positions by sessionChangeTime
      const sortedPositions = [...SessionTrack.hls.track.positions].sort(
        (a, b) => a.sessionChangeTime - b.sessionChangeTime
      );

      // Find the position that applies to the current time
      let currentPosition = sortedPositions[0]; // Default to first position

      for (let i = 0; i < sortedPositions.length; i++) {
        if (sortedPositions[i].sessionChangeTime <= currentTime) {
          currentPosition = sortedPositions[i];
        } else {
          break; // Stop once we find a position with change time greater than current time
        }
      }

      return currentPosition;
    };

    // Update positioning when currentTime changes
    useEffect(() => {
      if (SessionTrack?.hls?.track?.positions?.length > 0) {
        const newPosition = getCurrentPositioning();
        newPosition.aspectRatio = newPosition.height / newPosition.width;
        setPositioning(newPosition);
      }
    }, [currentTime, SessionTrack]);

    // Share the MediaStream from the main video element using captureStream
    useEffect(() => {
      const mainVideoRef = PlayerStore.hlsVideoRef;
      if (!mainVideoRef?.current || !videoRef.current) {
        return;
      }

      const mainVideo = mainVideoRef.current;
      const ourVideo = videoRef.current;

      try {
        // Check if the main video has loaded
        if (mainVideo.readyState >= 1) {
          // Use captureStream to get the decoded frames without duplicating network requests
          if (typeof mainVideo.captureStream === 'function') {
            const stream = mainVideo.captureStream();
            ourVideo.srcObject = stream;

            // Sync play/pause state
            if (isPlaying && ourVideo.paused) {
              ourVideo
                .play()
                .catch((e) => console.log('Error playing video:', e));
            }
          } else {
            console.error('captureStream not supported in this browser');
          }
        } else {
          // Wait for the main video to be ready
          const checkVideoReady = () => {
            if (mainVideo.readyState >= 1) {
              if (typeof mainVideo.captureStream === 'function') {
                const stream = mainVideo.captureStream();
                ourVideo.srcObject = stream;

                if (isPlaying && ourVideo.paused) {
                  ourVideo
                    .play()
                    .catch((e) => console.log('Error playing video:', e));
                }
              }
              mainVideo.removeEventListener('loadedmetadata', checkVideoReady);
            }
          };

          mainVideo.addEventListener('loadedmetadata', checkVideoReady);

          // Clean up
          return () => {
            mainVideo.removeEventListener('loadedmetadata', checkVideoReady);
          };
        }
      } catch (error) {
        console.error('Error sharing MediaStream:', error);
      }
    }, [PlayerStore.hlsVideoRef, isPlaying]);

    // Sync play/pause state
    useEffect(() => {
      if (!videoRef.current) return;

      if (isPlaying && videoRef.current.paused) {
        videoRef.current
          .play()
          .catch((e) => console.log('Error playing video:', e));
      } else if (!isPlaying && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    }, [isPlaying]);

    // Calculate the video style based on positioning and container size
    const updateVideoStyle = () => {
      if (!positioning || !viewPortSize.width || !viewPortSize.height) {
        setVideoStyle({
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        });
      }

      // The positioning data tells us which part of the video to show
      // We need to scale the video so that the region of interest fills the container

      // Calculate how much we need to scale the video
      const scaleWidth = viewPortSize.width / positioning.width;
      const scaleHeight = viewPortSize.height / positioning.height;

      // Use the same scale for both dimensions to maintain aspect ratio
      // We use the smaller scale to ensure the entire region fits in the viewport
      const scale = Math.min(scaleWidth, scaleHeight);

      // Calculate the dimensions of the entire video after scaling
      const videoWidth = 2560 * scale; // Assuming source video is 1920x1080
      const videoHeight = 1440 * scale;

      // Calculate the negative offset to position the region of interest
      const left = -(positioning.left * scale);
      const top = -(positioning.top * scale);

      // Update maxVideoSize
      setMaxVideoSize({
        width: positioning.width * scaleWidth,
        height: positioning.height * scaleHeight,
      });

      setVideoStyle({
        position: 'absolute',
        width: `${videoWidth}px`,
        height: `${videoHeight}px`,
        left: `${left}px`,
        top: `${top}px`,
      });
    };

    // Get the container style with aspect ratio preservation
    useEffect(() => {
      updateContainerDimensions();
    }, [positioning]);

    const updateContainerDimensions = () => {
      const style = {
        overflow: 'hidden',
        position: 'relative',
        margin: '0 auto',
      };
      //console.log('Parent size:', parentSize);
      //console.log('Positioning:', positioning);
      let containerDimensions = {};
      if (parentSize.width <= parentSize.height) {
        const adjustmentFactor = parentSize.width / positioning.width;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('width');
      } else {
        const adjustmentFactor = parentSize.height / positioning.height;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('height');
      }

      setViewPortSize(containerDimensions);
      setContainerStyle(style);
    };
    useEffect(() => {
      if (!videoContainerSize || !controlsContainerSize) return;
      const sidebarTrackCount = Object.keys(tracks).length - 1;
      if (isFocusedTrack) {
        const videoHeight =
          videoContainerSize.height - controlsContainerSize.height;
        const gapAdjustment = (sidebarTrackCount - 1) * 10;
        setVideoHeight(videoHeight + gapAdjustment);
      } else {
        const videoHeight =
          (videoContainerSize.height - controlsContainerSize.height) /
          sidebarTrackCount;
        //const gapAdjustment = (sidebarTrackCount - 1) * 5;
        setVideoHeight(videoHeight);
      }
    }, [videoContainerSize, controlsContainerSize]);

    return (
      <View
        as="div"
        id={SessionTrack?.id || 'viewport'}
        className={`hls-viewport ${resizeBasedOn}`}
        margin="auto"
        width={width}
        height={height}
        
        elementRef={(el) => {
          containerRef.current = el;
        }}
        style={{
          overflow: 'hidden',
          contain: 'strict',
        }}
        withVisualDebug={false}
      >
        {PlayerStore.hlsVideoRef && (
          <HlsViewer
            key={`hls-viewer-${SessionTrack?.id}-${isFocusedTrack ? 'focused' : 'sidebar'}-${seekCounter}`}
            hlsRef={PlayerStore.hlsVideoRef}
            regionBounds={positioning}
            showDebug={PlayerStore.showHlsViewerDebug}
            width={width}
            height={height}
            margin="auto"
            onLoadedData={() => {
              PlayerStore.setHlsPlaybackReady(true);
            }}
            onLoadedMetadata={() => {
              PlayerStore.setHlsPlaybackReady(true);
            }}
            onCanPlay={() => {
              PlayerStore.setHlsPlaybackReady(true);
            }}
          />
        )}
      </View>
    );
  }
);

Hls.propTypes = {
  SessionTrack: PropTypes.object.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
};

export default Hls;
